import {
  Children, cloneElement, createContext, isValidElement, useCallback, useContext, useId, useLayoutEffect, useMemo, useRef, useState,
  type ReactElement, type ReactNode,
} from 'react';

// What a field (FormField, DocumentShell's Field, a document header pair, a
// line-table column) tells the control inside it: the label's id (the control
// is named by it), the id the label points at, what describes the control, and
// whether it is required or invalid. Controls read it with useField() and only
// fill in what their caller left out, so an explicit id / aria-label wins.
export type FieldInfo = {
  ctlId?: string;
  labelId: string;
  describedBy?: string;
  required: boolean;
  invalid: boolean;
  // Claim-once control id: the first reader to claim gets ctlId, later readers
  // (a component holding several controls) get only aria-labelledby.
  claim?: (token: string) => boolean;
  // A control that is a group of controls (a multiselect's options) says so
  // while it is mounted (useFieldGroup): no control takes the control id,
  // so the label points at none.
  holdsGroup?: (on: boolean) => void;
};

export const FieldContext = createContext<FieldInfo | null>(null);

export function useField(): FieldInfo | null {
  return useContext(FieldContext);
}

type AriaBool = boolean | 'true' | 'false';
export type OwnAria = {
  id?: string;
  'aria-label'?: string;
  'aria-labelledby'?: string;
  'aria-describedby'?: string;
  'aria-invalid'?: AriaBool | 'grammar' | 'spelling';
  'aria-required'?: AriaBool;
};
export type ControlAria = {
  id?: string;
  'aria-labelledby'?: string;
  'aria-describedby'?: string;
  'aria-invalid'?: OwnAria['aria-invalid'];
  'aria-required'?: AriaBool;
};

// The attributes a control spreads AFTER its caller's props. `invalid` is the
// control's own invalid prop: aria-invalid follows it even outside a field.
export function fieldAria(f: FieldInfo | null, own: OwnAria, invalid?: boolean, token?: string): ControlAria {
  const out: ControlAria = {};
  const inv = own['aria-invalid'] ?? (invalid || f?.invalid ? true : undefined);
  if (inv !== undefined) out['aria-invalid'] = inv;
  if (!f) return out;
  const mayClaim = !own.id && !!f.ctlId && (!f.claim || !token || f.claim(token));
  const id = own.id ?? (mayClaim ? f.ctlId : undefined);
  if (id) out.id = id;
  if (own['aria-labelledby']) out['aria-labelledby'] = own['aria-labelledby'];
  else if (!own['aria-label']) out['aria-labelledby'] = f.labelId;
  const described = [own['aria-describedby'], f.describedBy].filter(Boolean).join(' ');
  if (described) out['aria-describedby'] = described;
  const req = own['aria-required'] ?? (f.required ? true : undefined);
  if (req !== undefined) out['aria-required'] = req;
  return out;
}

// For a control that is a group of controls named by the field's label (the
// group carries aria-labelledby): the field's label then points at no
// control (no htmlFor). Answers the field, for the group's own naming.
export function useFieldGroup(): FieldInfo | null {
  const f = useField();
  const mark = f?.holdsGroup;
  useLayoutEffect(() => {
    if (!mark) return undefined;
    mark(true);
    return () => mark(false);
  }, [mark]);
  return f;
}

export function useFieldAria(own: OwnAria, invalid?: boolean): ControlAria {
  const token = useId();
  return fieldAria(useField(), own, invalid, token);
}

const NATIVE_CONTROLS = new Set(['input', 'select', 'textarea']);

// Ids and context for one field. Only a SINGLE child gets the control id (a
// native input/select/textarea directly, through cloneElement; a component
// through the context); a wrapper with several controls gets none, so no id
// repeats; every control inside is still named through aria-labelledby.
export function useFieldSetup(opts: { children: ReactNode; required?: boolean; error?: ReactNode; hint?: ReactNode }) {
  const base = useId();
  const labelId = `${base}label`;
  const errorId = opts.error ? `${base}error` : undefined;
  const hintId = opts.hint ? `${base}hint` : undefined;
  const items = Children.toArray(opts.children);
  const only = items.length === 1 && isValidElement(items[0]) ? (items[0] as ReactElement<OwnAria>) : null;
  const native = only && typeof only.type === 'string' && NATIVE_CONTROLS.has(only.type) ? only : null;
  const isComponent = !!only && typeof only.type !== 'string';
  const ctlId = native || isComponent ? `${base}control` : undefined;
  const describedBy = [errorId, hintId].filter(Boolean).join(' ') || undefined;
  const claimed = useRef<string | null>(null);
  const claim = useCallback((t: string) => {
    if (claimed.current === null) claimed.current = t;
    return claimed.current === t;
  }, []);
  const required = !!opts.required;
  const invalid = !!opts.error;
  const [grouped, setGrouped] = useState(false);
  const info = useMemo<FieldInfo>(
    () => ({ ctlId: isComponent ? ctlId : undefined, labelId, describedBy, required, invalid, claim, holdsGroup: setGrouped }),
    [isComponent, ctlId, labelId, describedBy, required, invalid, claim],
  );
  const content = native ? cloneElement(native, fieldAria({ ...info, ctlId }, native.props)) : opts.children;
  return { info, labelId, errorId, hintId, labelFor: grouped ? undefined : ctlId, content };
}

// One label/value pair in a <dl> grid (document headers): the <dt> holds a
// real <label>, the <dd> publishes the FieldContext, so the control inside is
// named by the label. Used by DocumentShell's Field, DesignableHeader and the
// AR invoice header.
export function FieldPair({
  label, children, required,
  dtClassName = 'field-label whitespace-nowrap',
  ddClassName = 'min-w-0 zoom-gutter relative pr-5',
}: {
  label: ReactNode;
  children: ReactNode;
  required?: boolean;
  dtClassName?: string;
  ddClassName?: string;
}) {
  const { info, labelId, labelFor, content } = useFieldSetup({ children, required });
  return (
    <>
      <dt className={dtClassName}><label id={labelId} htmlFor={labelFor}>{label}{required && <RequiredMark />}</label></dt>
      <dd className={ddClassName}><FieldContext.Provider value={info}>{content}</FieldContext.Provider></dd>
    </>
  );
}

// The red asterisk is for the eye only; a screen reader hears "(required)".
export function RequiredMark() {
  return (
    <>
      <span aria-hidden="true" className="text-red-500 ml-0.5">*</span>
      {/* The space is its own text node: a space inside the sr-only span is
          dropped from the computed name ("Name(required)"). */}
      {' '}<span className="sr-only">(required)</span>
    </>
  );
}
